/**
 * Formats FQN (Fully Qualified Name) for Oracle database queries.
 *
 * Oracle FQN formats supported:
 * - "default.schema.table" → "schema.table" (removes default prefix)
 * - "catalog.schema.table" → "catalog.schema.table" (preserves catalog)
 * - "schema.table" → "schema.table" (preserves simple format)
 * - "table" → "table" (preserves single name)
 *
 * @param fqn - Fully Qualified Name to format
 * @returns Formatted FQN for Oracle, or empty string for invalid inputs
 *
 * @example
 * formatFqnForOracle('default.HR.EMPLOYEES') // returns 'HR.EMPLOYEES'
 * formatFqnForOracle('PROD.HR.EMPLOYEES') // returns 'PROD.HR.EMPLOYEES'
 * formatFqnForOracle('HR.EMPLOYEES') // returns 'HR.EMPLOYEES'
 * formatFqnForOracle('EMPLOYEES') // returns 'EMPLOYEES'
 */
export function formatFqnForOracle(fqn: string): string {
  // Validate input
  if (!fqn || typeof fqn !== 'string') {
    return ''
  }

  // Handle empty string after trimming
  const trimmedFqn = fqn.trim()
  if (!trimmedFqn) {
    return ''
  }

  const parts = trimmedFqn.split('.')

  // Handle FQNs with empty parts (e.g., "schema..table")
  if (parts.some((part) => part === '')) {
    // Return original for malformed FQNs to avoid data corruption
    return trimmedFqn
  }

  // Handle the specific case: default.schema.table -> schema.table
  if (parts.length === 3 && parts[0] === 'default') {
    return `${parts[1]}.${parts[2]}`
  }

  // For Oracle FQNs with catalog: catalog.schema.table -> return as is
  if (parts.length === 3 && parts[0] !== 'default') {
    return trimmedFqn
  }

  // For simple formats (schema.table or table), return as is
  if (parts.length <= 2) {
    return trimmedFqn
  }

  // For complex FQNs with more than 3 parts, be conservative
  if (parts.length > 3) {
    // If it starts with 'default', remove it and take the next two parts
    if (parts[0] === 'default') {
      return `${parts[1]}.${parts[2]}`
    }
    // Otherwise, return the original FQN to avoid data loss
    return trimmedFqn
  }

  return trimmedFqn
}
